import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class CorkMartPurchase extends HTMLElement {
  #controller;
  #generation = 0;
  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.section = this.closest('.shopify-section');
    this.section?.addEventListener(StandardEvents.productSelect, this.onVariant, options);
    this.section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.update, options);
    this.section?.addEventListener('input', this.onInput, options);
    this.addEventListener('click', this.onClick, options);
    this.update();
  }
  disconnectedCallback() { this.#controller?.abort(); }
  get quantityInput() { return this.section?.querySelector('product-form-component input[name="quantity"]'); }
  onInput = (event) => { if (event.target === this.quantityInput) this.update(); };
  onVariant = async (event) => {
    if (event.target.closest('product-card, quick-add-dialog')) return;
    const generation = ++this.#generation;
    const status = this.querySelector('[data-purchase-status]');
    status.textContent = this.dataset.loading;
    this.setAttribute('aria-busy', 'true');
    try {
      const { detail } = await event.promise;
      if (generation !== this.#generation) return;
      const source = detail?.html?.querySelector('cm-product-purchase');
      if (!source || String(detail.productId) !== this.dataset.productId) return;
      this.dataset.price = source.dataset.price;
      this.dataset.moneyFormat = source.dataset.moneyFormat;
      this.dataset.currency = source.dataset.currency;
      this.dataset.area = source.dataset.area;
      this.dataset.sample = source.dataset.sample;
      this.querySelector('[data-calculation-status]')?.replaceChildren();
      this.querySelector('[data-selection]').textContent = source.querySelector('[data-selection]').textContent;
      this.update();
      status.textContent = detail.cmAdjusted ? this.section.querySelector('variant-picker')?.dataset.adjustedMessage || '' : '';
    } catch (error) {
      if (generation === this.#generation && error.name !== 'AbortError') status.textContent = this.dataset.error;
    } finally {
      if (generation === this.#generation) this.removeAttribute('aria-busy');
    }
  };
  update = () => {
    this.hidden = this.dataset.sample === 'true';
    const quantity = Number(this.quantityInput?.value || 1);
    const price = Number(this.dataset.price);
    const total = this.querySelector('[data-total]');
    this.querySelector('[data-quantity]').textContent = `× ${quantity}`;
    if (!this.dataset.price || !Number.isFinite(quantity) || quantity < 1) total.textContent = '—';
    else total.textContent = formatMoney(price * quantity, this.dataset.moneyFormat, this.dataset.currency);
    const area = Number(this.dataset.area);
    const coverage = this.querySelector('[data-coverage]');
    if (coverage) {
      coverage.hidden = !(area > 0);
      this.querySelector('[data-area-total]').textContent = (area * quantity).toLocaleString(this.dataset.locale, { maximumFractionDigits: 3 });
    }
    const calculatorPanel = this.querySelector('.cm-area-calculator');
    if (calculatorPanel) calculatorPanel.hidden = this.dataset.sample === 'true' || !(area > 0);
    const calculator = this.querySelector('[data-calculate]');
    if (calculator) calculator.disabled = !(area > 0);
  };
  onClick = (event) => {
    if (!event.target.closest('[data-calculate]')) return;
    const areaInput = this.querySelector('[data-area-input]');
    if (!areaInput.value || !areaInput.reportValidity()) return;
    const input = this.quantityInput;
    const area = Number(this.dataset.area);
    if (!input || !(area > 0)) return;
    const min = Number(input.min || 1);
    const step = Number(input.step || 1);
    const quantity = min + Math.max(0, Math.ceil((Number(areaInput.value) / area - min) / step - 1e-9)) * step;
    input.value = String(quantity);
    if (!input.reportValidity()) return;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.update();
    this.querySelector('[data-calculation-status]').textContent = this.querySelector('[data-coverage]').textContent;
  };
}
if (!customElements.get('cm-product-purchase')) customElements.define('cm-product-purchase', CorkMartPurchase);
